import React, { useState } from 'react'
import { Card, Row, Col } from 'react-bootstrap'
import { Organization, useGetOrganizationBrandingPublicQuery } from '../../../services/orgsApi'
import styled from 'styled-components'
import { FaPencilAlt } from 'react-icons/fa'
import EditOrganizationModal from './EditOrganizationModal'

const HeaderCard = styled(Card)`
  background: #1a1a1a;
  color: white;
  border: none;
  margin-bottom: 1.5rem;
`

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
`

const OrganizationName = styled.h2`
  margin: 0;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`

const EditButton = styled.button<{ $variant: 'outline' | 'secondary' }>`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.5rem;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
  }

  &:focus {
    box-shadow: 0 0 0 0.25rem rgba(255, 255, 255, 0.25);
  }
`

interface OrganizationHeaderProps {
  organization: Organization
}

const OrganizationHeader: React.FC<OrganizationHeaderProps> = ({ organization }) => {
  const { data: brandingBlob } = useGetOrganizationBrandingPublicQuery()
  const [logoUrl, setLogoUrl] = useState<string | undefined>()
  const [showEditModal, setShowEditModal] = useState(false)

  // Create object URL from blob when branding data changes
  React.useEffect(() => {
    if (brandingBlob) {
      const url = URL.createObjectURL(brandingBlob)
      setLogoUrl(url)

      // Return cleanup function for the newly created URL
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setLogoUrl(undefined)
    }
  }, [brandingBlob])

  return (
    <>
      <HeaderCard>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col xs="auto">
              {logoUrl ? (
                <LogoImage src={logoUrl} alt={`${organization.name} Logo`} />
              ) : (
                <div
                  style={{
                    height: '60px',
                    width: '60px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="fas fa-building" style={{ fontSize: '24px', opacity: 0.7 }}></i>
                </div>
              )}
            </Col>
            <Col>
              <OrganizationName>{organization.name}</OrganizationName>
            </Col>
            <Col xs="auto">
              <EditButton
                $variant="outline"
                onClick={() => setShowEditModal(true)}
                title="Edit Organization"
              >
                <FaPencilAlt />
              </EditButton>
            </Col>
          </Row>
        </Card.Body>
      </HeaderCard>

      <EditOrganizationModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        organization={organization}
      />
    </>
  )
}

export default OrganizationHeader
